/**
 * Armor system types for Rogue Trader
 * Handles body location-based armor values and stacking rules
 */

// One declaration only - imported and re-exported so the armour system keeps a
// local import path while there is a single source of truth in types/character.ts.
import { BodyLocation } from './character';

export { BodyLocation };

export interface ArmorValue {
  location: BodyLocation;
  armorPoints: number;
  source: string; // Name of the armor piece providing this protection
}

export interface ArmorPiece {
  id: string;
  name: string;
  coverage: Map<BodyLocation, number>; // Location -> AP value
  special: string[];
  weight: number;
  /** Whether the piece is donned. Renamed from `equipped` 2026-08-04. */
  worn: boolean;
}

export interface TotalArmorValues {
  head: number;
  leftArm: number;
  rightArm: number;
  body: number;
  leftLeg: number;
  rightLeg: number;
}

/**
 * Parse armor description to extract AP values and locations
 * Format examples:
 * - "AP 2 - Head"
 * - "AP 3 - Arms, Body, Legs"
 * - "AP 4 - All"
 */
export function parseArmorDescription(description: string): Map<BodyLocation, number> {
  const coverage = new Map<BodyLocation, number>();

  // Extract AP value
  const apMatch = description.match(/AP\s+(\d+)/i);
  if (!apMatch) return coverage;

  const apValue = parseInt(apMatch[1]);

  // Extract locations
  const locationPart = description.split('-')[1]?.trim().toLowerCase();
  if (!locationPart) return coverage;

  // Handle "All" case
  if (locationPart.includes('all')) {
    Object.values(BodyLocation).forEach((loc) => {
      coverage.set(loc, apValue);
    });
    return coverage;
  }

  // Map description terms to body locations
  const locationMap: Record<string, BodyLocation[]> = {
    head: [BodyLocation.Head],
    arms: [BodyLocation.LeftArm, BodyLocation.RightArm],
    arm: [BodyLocation.LeftArm, BodyLocation.RightArm],
    body: [BodyLocation.Body],
    legs: [BodyLocation.LeftLeg, BodyLocation.RightLeg],
    leg: [BodyLocation.LeftLeg, BodyLocation.RightLeg]
  };

  // Parse each location mentioned
  Object.entries(locationMap).forEach(([term, locations]) => {
    if (locationPart.includes(term)) {
      locations.forEach((loc) => {
        coverage.set(loc, apValue);
      });
    }
  });

  return coverage;
}

/**
 * Reinforced special rule: +1 AP on each location the piece covers (RT Core, Ch05 Armoury).
 */
function reinforcedApBonus(piece: ArmorPiece): number {
  return piece.special?.some((s) => s.toLowerCase() === 'reinforced') ? 1 : 0;
}

/**
 * Effective carried weight of an item. The Reinforced armour quality increases
 * the piece's weight by 50% (RT Core, Ch05 Armoury).
 */
export function effectiveWeight(item: { weight: number; special?: string[] }): number {
  const reinforced = item.special?.some((s) => s.toLowerCase() === 'reinforced');
  return reinforced ? item.weight * 1.5 : item.weight;
}

/**
 * Calculate total armor values across all equipped armor pieces
 * Implements Rogue Trader armor stacking rules:
 * - Multiple armor pieces on same location: only highest value counts
 * - Exception: Some special armors may stack (noted in special rules)
 */
export function calculateTotalArmor(armorPieces: ArmorPiece[]): TotalArmorValues {
  const maxValues: TotalArmorValues = {
    head: 0,
    leftArm: 0,
    rightArm: 0,
    body: 0,
    leftLeg: 0,
    rightLeg: 0
  };

  // Only consider worn armor
  const equippedArmor = armorPieces.filter((piece) => piece.worn);

  // For each location, take the maximum AP value
  equippedArmor.forEach((piece) => {
    const bonus = reinforcedApBonus(piece);
    piece.coverage.forEach((apValue, location) => {
      const ap = apValue + bonus;
      if (ap > maxValues[location]) {
        maxValues[location] = ap;
      }
    });
  });

  return maxValues;
}

/**
 * Get armor breakdown showing which pieces contribute to each location
 */
export function getArmorBreakdown(armorPieces: ArmorPiece[]): Map<BodyLocation, ArmorValue[]> {
  const breakdown = new Map<BodyLocation, ArmorValue[]>();

  // Initialize all locations
  Object.values(BodyLocation).forEach((loc) => {
    breakdown.set(loc, []);
  });

  // Only consider equipped armor
  const equippedArmor = armorPieces.filter((piece) => piece.worn);

  // Collect all armor values for each location
  equippedArmor.forEach((piece) => {
    const bonus = reinforcedApBonus(piece);
    piece.coverage.forEach((apValue, location) => {
      const values = breakdown.get(location) || [];
      values.push({
        location,
        armorPoints: apValue + bonus,
        source: piece.name
      });
      breakdown.set(location, values);
    });
  });

  // Sort by AP value (highest first) for each location
  breakdown.forEach((values, location) => {
    values.sort((a, b) => b.armorPoints - a.armorPoints);
    breakdown.set(location, values);
  });

  return breakdown;
}

/**
 * Display names for body locations
 */
export const BODY_LOCATION_NAMES: Record<BodyLocation, string> = {
  [BodyLocation.Head]: 'Head',
  [BodyLocation.LeftArm]: 'Left Arm',
  [BodyLocation.RightArm]: 'Right Arm',
  [BodyLocation.Body]: 'Body',
  [BodyLocation.LeftLeg]: 'Left Leg',
  [BodyLocation.RightLeg]: 'Right Leg'
};
